/**
 * PublicNavbar - Navigation bar za javne stranice sa Dashboard3 dizajnom
 *
 * Glass morphism navbar sa Dashboard3 styling-om.
 * Podržava desktop i mobile navigaciju (drawer za mobile).
 * Theme toggle je na desktopu fiksiran u gornjem desnom uglu (PublicLayout); na mobilnom je u drawer-u.
 *
 * @example
 * ```tsx
 * <PublicNavbar />
 * <PublicNavbar hideLinks={true} />
 * ```
 */

"use client";

import { Box, Flex, Icon } from "@chakra-ui/react";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import { useMemo } from "react";
import { HiOutlineBars3, HiOutlineXMark } from "react-icons/hi2";
import { getPublicNavbarLinks } from "@/components";
import { TenantLogo } from "@/components/shared/TenantLogo";
import { Button, Card, IconButton, Link } from "@/components/ui";
import {
  useMediaAssetGet,
  usePresignedImageErrorRecovery,
} from "@/features/media/hooks";
import {
  usePublicLocationsNavLink,
  usePublicTicketsNavLink,
  useTenantData,
} from "@/hooks";
import { useThemeTokens } from "@/lib/theme/tokens";
import { PageTypeGlobal, PageTypePortal } from "@/lib/types";
import { getTenantDisplayName } from "@/lib/utils/tenant";
import { EntryType, MediaGroup } from "@/openapi/generated/models";

type PublicNavbarProps = {
  /**
   * Sakrije navigacione linkove (koristi se za error stranice)
   */
  hideLinks?: boolean;
  isMenuOpen?: boolean;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
};

export const PublicNavbar = ({
  hideLinks = false,
  isMenuOpen = false,
  onMenuOpen,
  onMenuClose,
}: PublicNavbarProps) => {
  const pathname = usePathname();
  const theme = useThemeTokens();
  const { tenantData, tenantId } = useTenantData();
  const { data: session } = useSession();
  const isMember = !!session?.user?.isMember;

  // Get dynamic tenant data
  const tenantName = getTenantDisplayName(tenantData);

  // Fetch tenant logo from media API
  // TODO: Set requireAuth: false when backend supports public media endpoint
  const { url: logoUrl, refetch: refetchLogo } = useMediaAssetGet(
    {
      tenantId,
      ownerType: EntryType.Tenant,
      ownerId: tenantId,
      mediaGroup: MediaGroup.Logo,
    },
    { enabled: !!tenantId, requireAuth: false, refetchOnMount: false },
  );
  const handleLogoError = usePresignedImageErrorRecovery(logoUrl, refetchLogo);

  const isErrorPage =
    pathname === "/forbidden" || pathname === "/403" || pathname === "/404";
  const shouldShowLinks = !isErrorPage && !hideLinks;

  const logoHref = isMember ? PageTypePortal.Dashboard : PageTypeGlobal.Home;

  const locationsNav = usePublicLocationsNavLink();
  const ticketsNav = usePublicTicketsNavLink();

  const linkItems = useMemo(
    () =>
      shouldShowLinks
        ? getPublicNavbarLinks(isMember, pathname ?? "", {
            locationsNav,
            ticketsNav,
          })
        : [],
    [shouldShowLinks, pathname, isMember, locationsNav, ticketsNav],
  );

  const { marketingLinks, loginLink, signUpLink } = useMemo(() => {
    const login = linkItems.find((item) => item.href === PageTypePortal.Login);
    const signUp = linkItems.find(
      (item) => item.href === PageTypePortal.SignUp,
    );
    const marketing = linkItems.filter(
      (item) =>
        item.href !== PageTypePortal.Login &&
        item.href !== PageTypePortal.SignUp,
    );
    return {
      marketingLinks: marketing,
      loginLink: login,
      signUpLink: signUp,
    };
  }, [linkItems]);

  const handleMenuClick = () => {
    if (isMenuOpen) {
      onMenuClose?.();
    } else {
      onMenuOpen?.();
    }
  };

  // Shared logo wrapper styles
  const logoWrapperStyles = {
    transition: "opacity 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    sx: {
      filter: theme.mode === "dark" ? "brightness(1.1)" : "none",
      "@media (hover: hover)": {
        "&:hover": {
          opacity: shouldShowLinks ? 0.8 : 1,
        },
      },
    },
    flexShrink: 0,
    position: "relative" as const,
  };

  // Logo component props
  const logoProps = {
    width: 60,
    height: 53,
    tenantData,
    src: logoUrl,
    alt: "",
    priority: true,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    onError: handleLogoError,
    style: {
      filter: theme.mode === "dark" ? "brightness(1.1)" : "none",
    },
  };

  return (
    <Box
      mx="auto"
      left="50%"
      zIndex={10}
      w="1044px"
      position="fixed"
      maxW="calc(100% - 2rem)"
      transform="translateX(-50%)"
      top={{ base: "20px", md: "20px" }}
    >
      <Card
        p={4}
        minH="76px"
        w="100%"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        bg={"none"}
      >
        <Flex
          alignItems="center"
          justify="space-between"
          gap="16px"
          width="100%"
          flexWrap={{ base: "nowrap", lg: "wrap" }}
        >
          <Link href={logoHref} aria-label={`${tenantName} home`}>
            <Box {...logoWrapperStyles}>
              <TenantLogo {...logoProps} />
            </Box>
          </Link>

          {shouldShowLinks ? (
            <Flex
              display={{ base: "none", lg: "flex" }}
              gap="8px"
              align="center"
              flex="1"
              justify="center"
              flexWrap="wrap"
              minW={0}
            >
              {marketingLinks.map(({ href, label, active }) => (
                <Link key={href} href={href}>
                  <Box
                    p="10px 14px"
                    fontSize="sm"
                    fontWeight="500"
                    position="relative"
                    color={active ? theme.accent.primary : theme.text.primary}
                    transition="all 0.2s ease"
                    borderRadius="8px"
                    whiteSpace="nowrap"
                    flexShrink={0}
                    bg={active ? theme.accent.primaryBg : "transparent"}
                    sx={{
                      "@media (hover: hover)": {
                        "&:hover": {
                          bg: theme.accent.mutedBg,
                          color: theme.accent.primary,
                        },
                      },
                    }}
                  >
                    {label}
                  </Box>
                </Link>
              ))}
            </Flex>
          ) : null}

          <Flex
            display={{ base: "none", lg: "flex" }}
            align="center"
            gap={2}
            flexShrink={0}
          >
            {loginLink ? (
              <Button
                as={Link}
                href={loginLink.href}
                variant="outline"
                size="sm"
                h="32px"
                px={4}
                fontWeight="500"
                border={"none"}
              >
                {loginLink.label}
              </Button>
            ) : null}
            {signUpLink ? (
              <Button
                as={Link}
                href={signUpLink.href}
                variant="primary"
                size="sm"
                h="32px"
                px={4}
                fontWeight="500"
              >
                {signUpLink.label}
              </Button>
            ) : null}
          </Flex>

          {/* Mobile: Menu Button (only if links are shown) */}
          <Flex
            display={{ base: "flex", lg: "none" }}
            gap="8px"
            align="center"
            flexShrink={0}
          >
            {shouldShowLinks && (
              <IconButton
                aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                icon={
                  isMenuOpen ? (
                    <Icon as={HiOutlineXMark} />
                  ) : (
                    <Icon as={HiOutlineBars3} />
                  )
                }
                variant="ghost"
                onClick={handleMenuClick}
                color={theme.text.secondary}
                borderWidth="1px"
                borderColor={theme.borderGlass.default}
                borderRadius="8px"
                sx={{
                  WebkitTapHighlightColor: "transparent",
                  "@media (hover: hover)": {
                    "&:hover": {
                      bg: theme.surfaceGlass.cardStrong,
                      borderColor: theme.accent.primaryBorder,
                      color: theme.accent.primary,
                    },
                  },
                  "@media (hover: none) and (pointer: coarse)": {
                    "&:hover, &:active, &:focus, &:focus-visible, &[data-active]":
                      {
                        bg: "transparent",
                        borderColor: theme.borderGlass.default,
                        color: theme.text.secondary,
                        boxShadow: "none",
                        outline: "none",
                      },
                  },
                }}
              />
            )}
          </Flex>
        </Flex>
      </Card>
    </Box>
  );
};
